import React from 'react';
import { LineChart as _LC, Line as _L, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Area, AreaChart, ComposedChart } from 'recharts';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import useFinanceData from './useFinanceData';

// Recharts inline-prop constants — hoisted to avoid re-creating objects on
// every render (perf: prevents needless chart sub-tree reconciliation).
const AXIS_TICK = { fontSize: 10 };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const BAR_RADIUS_SMALL = [2, 2, 0, 0];
const BAR_RADIUS = [3, 3, 0, 0];

/**
 * Finance — onglet Vue d'ensemble. Trois graphiques : évolution
 * trésorerie (30 derniers jours), barres revenus vs dépenses
 * journalières, agrégation mensuelle. Extracted from Finance.jsx on
 * 28/05/2026.
 */
export default function OverviewTab() {
  const { t } = useTranslation();
  const { chartData, monthlyHistory } = useFinanceData();

  return (
    <div className="space-y-4">
      {/* Treasury Evolution */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3">{t('treasury_chart')}</h3>
        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorTres" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1F3864" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#1F3864" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={AXIS_TICK} stroke="#9CA3AF" />
              <YAxis tick={AXIS_TICK} stroke="#9CA3AF" />
              <Tooltip formatter={(v) => [formatMoney(v), '']} contentStyle={TOOLTIP_STYLE} />
              <Area type="monotone" dataKey="tresorerie" stroke="#1F3864" strokeWidth={2} fill="url(#colorTres)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[220px] flex items-center justify-center text-slate-500 text-sm">{t('insufficient_data')}</div>
        )}
      </div>

      {/* Daily Revenue vs Expenses */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3">{t('revenue_vs_expenses')}</h3>
        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={AXIS_TICK} stroke="#9CA3AF" />
              <YAxis tick={AXIS_TICK} stroke="#9CA3AF" />
              <Tooltip formatter={(v, name) => [formatMoney(v), name === 'revenus' ? 'Revenus' : 'Dépenses']} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="revenus" fill="#10B981" radius={BAR_RADIUS_SMALL} barSize={8} />
              <Bar dataKey="depenses" fill="#EF4444" radius={BAR_RADIUS_SMALL} barSize={8} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[180px] flex items-center justify-center text-slate-500 text-sm">{t('insufficient_data')}</div>
        )}
      </div>

      {/* Monthly aggregate */}
      {monthlyHistory.length > 1 && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-3">Bilan mensuel</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={monthlyHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={AXIS_TICK} stroke="#9CA3AF" />
              <YAxis tick={AXIS_TICK} stroke="#9CA3AF" />
              <Tooltip formatter={(v) => [formatMoney(Math.round(v)), '']} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="revenus" fill="#10B981" name="Revenus" radius={BAR_RADIUS} />
              <Bar dataKey="depenses" fill="#EF4444" name="Dépenses" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
